import React from "react";
import PropTypes from "prop-types";
import { useSelector } from "react-redux";
import { useForm } from "react-hook-form";
import { Oval } from "react-loader-spinner";
import { useTranslation } from "react-i18next";
import { useLookupsContext } from "hooks/useContext";
import Input from "components/input";
import Select from "components/select";
import { validateEmail } from "utils/helper";

function EditEmergencyContact({
  onDiscard = () => {},
  onSave = () => {},
  isLoading = false,
}) {
  const [t] = useTranslation();
  const userDetails = useSelector((state) => state.userDetails || {});
  const { countries } = useLookupsContext(false);

  const defaultsValues = {
    name: userDetails?.extraInformation?.emergencyContactDetails?.name ?? "",
    relationship:
      userDetails?.extraInformation?.emergencyContactDetails?.relationship ??
      "",
    email: userDetails?.extraInformation?.emergencyContactDetails?.email ?? "",
    emergencyCountry:
      userDetails?.extraInformation?.emergencyContactDetails?.country ?? "",
    emergencyPhoneNumber:
      userDetails?.extraInformation?.emergencyContactDetails?.mobileNumber ??
      "",
    emergencyHomeTelephone:
      userDetails?.extraInformation?.emergencyContactDetails?.homeTelephone ??
      "",
  };

  const countriesOptions = countries.map((elem) => ({
    ...elem,
    label: elem.name,
    value: elem.id,
  }));

  const {
    register,
    handleSubmit,
    setValue,
    watch,
    formState: { errors },
  } = useForm({
    mode: "onChange",
    defaultValues: defaultsValues,
  });

  return (
    <form
      className="flex flex-col self-stretch rounded-[16px] border-[1px] border-solid border-primary"
      onSubmit={handleSubmit(onSave)}
    >
      <div className="flex p-[24px] justify-between align-center self-stretch border-b border-solid border-[#E4E4E7]">
        <div className="text-[#1D1B22] font-semibold text-[24px]">
          {t("emergencyContactTitle")}
        </div>

        <div className="flex items-center gap-[12px]">
          <button
            type="button"
            onClick={onDiscard}
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-default-color bg-white h-[46px]"
          >
            <div className="text-base-black font-bold text-[16px] not-italic">
              {t("Cancel")}
            </div>
          </button>
          <button
            type="submit"
            disabled={isLoading}
            className="cursor-pointer flex py-[14px] px-[16px] gap-[8px] justify-center items-center rounded-[6px] border-[1px] border-solid border-primary bg-primary h-[46px]"
          >
            <Oval
              height={30}
              width={30}
              color="#FFF"
              visible={isLoading}
              ariaLabel="oval-loading"
              secondaryColor="#FFF"
              strokeWidth={5}
              strokeWidthSecondary={3}
            />
            <div className="text-white font-bold text-[16px] not-italic">
              {t("saveChangesCta")}
            </div>
          </button>
        </div>
      </div>

      <div className="flex flex-wrap gap-[20px] p-[24px] self-stretch">
        {/* ************** NAME ***************/}
        <Input
          label={t("name")}
          isRequired={true}
          placeholder={t("placeHolderType", {
            title: t("name").toLowerCase(),
          })}
          name="name"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("name") }),
          }}
          errorMessage={errors.name ? errors.name.message : ""}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.name ? "border-red" : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />

        {/* ************** RELATIONSHIP ***************/}
        <Input
          label={t("relationship")}
          isRequired={true}
          placeholder={t("placeHolderType", {
            title: t("relationship").toLowerCase(),
          })}
          name="relationship"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("relationship") }),
          }}
          errorMessage={errors.relationship ? errors.relationship.message : ""}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.relationship ? "border-red" : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />

        {/* ************** EMAIL ***************/}
        <Input
          label={t("email")}
          isRequired={true}
          placeholder={t("placeHolderType", {
            title: t("email").toLowerCase(),
          })}
          name="email"
          type="email"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("email") }),
            validate: validateEmail,
          }}
          errorMessage={errors.email ? errors.email.message : ""}
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.email ? "border-red" : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />

        {/* ************** COUNTRY ***************/}
        <Select
          label={t("country")}
          placeholder={t("placeholderSelect", {
            field: t("country").toLowerCase(),
          })}
          name={"emergencyCountry"}
          containerClassName="flex flex-col gap-[10px] min-w-[250px] sm-max:w-full"
          value={
            watch("emergencyCountry")
              ? countriesOptions.find(
                  (elem) => elem.name === watch("emergencyCountry")
                )
              : null
          }
          onChange={(e) => {
            setValue("emergencyCountry", e.label);
          }}
          isRequired={true}
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("country") }),
          }}
          errorMessage={
            errors.emergencyCountry ? errors.emergencyCountry.message : ""
          }
          options={countriesOptions}
        />

        {/* ************** PHONE NUMBER ***************/}
        <Input
          label={t("phoneNumber")}
          isRequired={true}
          placeholder={t("phonePlaceholder")}
          name="emergencyPhoneNumber"
          type="text"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("phoneNumber") }),
          }}
          errorMessage={
            errors.emergencyPhoneNumber
              ? errors.emergencyPhoneNumber.message
              : ""
          }
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.emergencyPhoneNumber ? "border-red" : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />

        {/* ************** HOME TELEPHONE ***************/}
        <Input
          label={t("homeTelephone")}
          isRequired={true}
          placeholder={t("phonePlaceholder")}
          name="emergencyHomeTelephone"
          type="text"
          register={register}
          registerArgs={{
            required: t("errorField", { field: t("homeTelephone") }),
          }}
          errorMessage={
            errors.emergencyHomeTelephone
              ? errors.emergencyHomeTelephone.message
              : ""
          }
          labelClassName="text-primary font-semibold text-[16px]"
          inputClassName={`flex h-[43px] min-w-[250px] sm-max:w-full items-center gap-[8px] self-stretch rounded-[6px] border-[1px] border-solid ${
            errors.emergencyHomeTelephone
              ? "border-red"
              : "border-default-color"
          } bg-transparent outline-none text-dark-gray font-regular text-[16px] not-italic placeholder:font-regular placeholder:text-[16px] placeholder:not-italic placeholder:font-normal py-[16px] px-[14px]`}
        />
      </div>
    </form>
  );
}

EditEmergencyContact.propTypes = {
  onDiscard: PropTypes.func,
  onSave: PropTypes.func,
  isLoading: PropTypes.bool,
};

export default EditEmergencyContact;